/* eslint-disable arrow-body-style */
// index.tsx
import React, { useMemo, forwardRef, useRef } from 'react';
import './index.scss';
import { extractMetricDataKey, getDimensionName, getDissionLable } from '../AIContent/_utils';
import { IDimensionInfo, IMetricInfo, IShowFilterItem } from '../AIContent/hooks';
import SingleSelect from '@home/components/SingleSelect';
import { getLocale } from 'umi';

interface Props {
  onChange: (values: Record<string, string>, isMetrics: boolean) => void;
  dimensionInfo: IDimensionInfo[];
  filterInfoVal: { [key: string]: string; } | null;
  currentMetric: string;
  showFilters: IShowFilterItem[];
  showMetrics: IMetricInfo[];
  isFullScreen?: boolean; // 是否全屏状态
}

const FilterComponent = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { filterInfoVal, onChange, currentMetric, dimensionInfo, showFilters, showMetrics, isFullScreen = false } = props;
  
  // 创建一个容器 ref 用于 popupContainer
  const containerRef = useRef<HTMLDivElement>(null);
  
  // getPopupContainer 函数：全屏时直接渲染到 body，否则使用容器
  const getPopupContainer = () => {
    // 全屏状态下，直接渲染到 body，避免 Portal 容器的层级问题
    if (isFullScreen) {
      return document.body;
    }
    return containerRef.current || document.body;
  };
  
  const filterDataList = useMemo(() => (
    showFilters?.map((items: IShowFilterItem) => {
      const key = items?.data_key || '';
      const valueList = items?.value || [];
      const filterName = items?.name || getDimensionName(key, dimensionInfo) || key;
      return {
        ...items,
        defaultValue: valueList?.[0]?.value || valueList?.[0],
        name: filterName,
        options: valueList?.map((item: ({ [key: string]: string; } | string)) => {
          if (typeof item === 'object' && item !== null) {
            const displayLabel = getDissionLable(key, item?.value, item?.name);
            return {
              label: displayLabel || item?.value,
              text: displayLabel || item?.value,
              value: item?.value,
              icon: item?.icon || '',
              icon_active: item?.icon_active || '',
            };
          }
          const displayLabel = getDissionLable(key, item);
          return { label: displayLabel || item,  text: displayLabel || item, value: item };
        })
      };
    })
  ), [showFilters, dimensionInfo]);

  const filterMetricsList = useMemo(() => {
    const firstMetric = showMetrics?.[0];
    const firstKey = extractMetricDataKey(firstMetric?.data_key) || firstMetric?.metric_key || '';

    return {
      data_key: 'metrics',
      defaultValue: firstKey,
      name: 'Metrics',
      type: 'numerical',
      options: showMetrics?.map((item) => {
        const dataKey = extractMetricDataKey(item?.data_key) || item?.metric_key || '';
        return {
          ...item,
          value: dataKey,
          text: item?.name || dataKey,
        };
      }),
    };
  }, [showMetrics]);

  const hideMetrics = useMemo(() => {
    const realtimeMetricOptions = filterDataList?.filter(item => item.data_key ==="realtime_metric") || [];
    // realtime_metric的时候，后端返回的bi_data中，会把指标当做dimisson_info处理。这时候会出现两个指标名称（一个筛选器，一个指标名称），在这里对指标的展示进行处理
    const realtimeMetricLength = realtimeMetricOptions?.[0]?.value?.length;
    return  (realtimeMetricLength >= 1);
  }, [filterDataList]);

  return (
    <div ref={(node) => {
      // 同时设置两个 ref
      containerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    }} className="dba-may-ask-filter-container">
      {
        filterDataList?.map((item, index) => {
          const { data_key, name, options} = item || {};
          if(options?.length <= 1) {
            return null;
          }
          return <SingleSelect
            key={data_key + index}
            className='dba-may-ask-filter-item'
            overlayClassName='dba-may-ask-filter-item-overlay'
            options={options || []}
            value={filterInfoVal?.[data_key] || ''}
            listWidth={0}
            matchButtonWidth
            searchable={false}
            locale={getLocale()}
            onChange={(value) => {
              onChange({
                [data_key]: value
              }, false);
            }}
            name={name} // 这里负责展示的metrics
            // 将下拉菜单渲染到容器内，避免被 DragResizeBox 的样式影响
            getPopupContainer={getPopupContainer}
          />;
        })
      }
      {
        !hideMetrics && showMetrics && filterMetricsList?.options?.length > 1 && <SingleSelect
          options={filterMetricsList?.options || []}
          className='dba-may-ask-filter-item'
          overlayClassName='dba-may-ask-filter-item-overlay'
          value={currentMetric}
          listWidth={0}
          matchButtonWidth
          locale={getLocale()}
          searchable={false}
          onChange={(value) => {
            onChange({
              metrics: value
            }, true);
          }}
          name={'Metrics'} // 这里负责展示的metrics
          // 将下拉菜单渲染到容器内，避免被 DragResizeBox 的样式影响
          getPopupContainer={getPopupContainer}
        />
      }

    </div>
  );
});

export default FilterComponent;